export const CHAR_TYPE_MAP = {
  hiragana: {
    label: "ひらがな",
    /**
     * \u3000: Full-width space
     * \u3041-\u3094: Basic Hiragana characters (ぁ to ん)
     * \u309B-\u309E: Voicing marks (Dakuten/Handakuten ゛ ゜) and Iteration marks (ゝ ゞ)
     * \u30FB-\u30FC: Middle dot (・) and Prolonged sound mark (ー)
     */
    regex: /[\u3000\u3041-\u3094\u309B-\u309E\u30FB-\u30FC]/g,
  },
  fullWidthKatakana: {
    label: "全角カナ",
    /**
     * \u3000: Full-width space
     * \u30A1-\u30F6: Basic Full-width Katakana characters (ァ to ヶ)
     * \u30FC: Prolonged sound mark (ー)
     */
    regex: /[\u3000\u30A1-\u30F6\u30FC]/g,
  },
  kanji: {
    label: "漢字",
    /**
     * \u4E00-\uA000: CJK Unified Ideographs Block (Covers all common and extended Kanji)
     */
    regex: /[\u4E00-\uA000]/g,
  },
  fullWidthAlpha: {
    label: "全角英字",
    regex: /[Ａ-Ｚａ-ｚ]/g,
  },
  fullWidthDigit: {
    label: "全角数字",
    regex: /[０-９]/g,
  },
  fullWidthSymbol: {
    label: "全角記号",
    /**
     * \u3000: Full-width space
     * \u3000-\u303F: CJK punctuation and symbols (、 。 「 」...)
     * \uFF01-\uFF5E: Full-width variants of ASCII characters (brackets, math symbols ＋ － ＝...)
     * Other discrete codes: Arrows (→ ←), geometric shapes (○ △ □ ★), temperature (℃), currency (￥ ＄), etc.
     */
    regex:
      /[\u3000\u309B-\u309E\u30FB\u30FC\u30FD\u30FE\u3001\u3002\uFF0C\uFF0E\uFF1A\uFF1B\uFF01\u00B4\uFF40\u00A8\uFF3E\uFFE3\uFF3F\u3003\u4EDD\u3005-\u3007\u2015\u2010\uFF0F\uFF3C\uFF5E\u2225\uFF5C\u2026\u2025\u2018\u2019\u201C\u201D\uFF08\uFF09\u3014\u3015\uFF3B\uFF3D\uFF5B\uFF5D\u3008-\u3011\uFF0B\uFF0D\u00B1\u00D7\u00F7\uFF1D\u2260\uFF1C\uFF1E\u2266\u2267\u221E\u2234\u2642\u2640\u00B0\u2032\u2033\u2103\uFFE5\uFF04\uFFE0\uFFE1\uFF05\uFF03\uFF06\uFF20\u00A7\u2606\u2605\u25CB\u25CF\u25CE\u25C7\u25C6\u25A1\u25B3\u25B2\u25BD\u25BC\u203B\u3012\u2192\u2190\u2191\u2193\u3013\u25A0\uFF1F\uFF0A]/g,
  },
  halfWidthKatakana: {
    label: "半角カナ",
    /**
     * \uFF61-\uFF9F: Half-width Katakana variants
     */
    regex: /[\uFF61-\uFF9F]/g,
  },
  halfWidthDigit: { label: "半角数字", regex: /[0-9]/g },
  halfWidthAlpha: { label: "半角英字", regex: /[A-Za-z]/g },
  halfWidthUpperAlpha: { label: "半角英大文字", regex: /[A-Z]/g },
  halfWidthSymbol: {
    label: "半角記号",
    /**
     * \u0020: Half-width space
     * \u0021-\u002F, \u003A-\u0040, \u005B-\u0060, \u007B-\u007E: Standard ASCII symbols (!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~)
     * \uFF61-\uFF65: Half-width CJK punctuation (｡ ｢ ｣ ､ ･)
     * \uFF70: Half-width prolonged sound mark (ｰ)
     * \uFF9E-\uFF9F: Half-width voicing marks (ﾞ ﾟ)
     */
    regex: /[\u0020\u0021-\u002F\u003A-\u0040\u005B-\u0060\u007B-\u007E\uFF61-\uFF65\uFF70\uFF9E-\uFF9F]/g,
  },
  halfWidthSymbolNoQuotes: {
    label: "半角記号（厳密）",
    /**
     * Strict version: Straight double quotes (") and straight single quotes (') are excluded (for automatic conversion to full-width fields).
     * \u0020: Half-width space
     * \u0021: Exclamation mark (!)
     * \u0023-\u0026: # $ % & (Skipped \u0022 which is ")
     * \u0028-\u002F: ( ) * + , - . / (Skipped \u0027 which is ')
     * The remaining ranges keep the standard ASCII symbols and half-width Katakana punctuation intact.
     */
    regex: /[\u0020\u0021\u0023-\u0026\u0028-\u002F\u003A-\u0040\u005B-\u0060\u007B-\u007E\uFF61-\uFF65\uFF70\uFF9E-\uFF9F]/g,
  },
  halfWidthPeriod: { label: "半角ピリオド", regex: /\./g },
  halfWidthComma: { label: "半角カンマ", regex: /,/g },
  halfWidthHyphen: { label: "半角ハイフン", regex: /-/g },
  halfWidthSpace: { label: "半角スペース", regex: / /g },
  fullWidthSpace: {
    label: "全角スペース",
    /**
     * \u3000: Full-width space
     */
    regex: /[\u3000]/g,
  },
  all: {
    label: "すべて",
    /**
     * [\s\S]: Matches any character, including newlines (equivalent to VBA's empty string replacement logic)
     */
    regex: /[\s\S]/g,
  },
} as const;

export type TCharTypeKey = keyof typeof CHAR_TYPE_MAP;

export type TCharTypeValidationOptions = TCharTypeKey[];

// Full-width characters and half-width characters that can be converted to full-width
export const AUTO_CONVERT_FULL_WIDTH_CHAR_TYPES: TCharTypeValidationOptions = [
  "hiragana",
  "kanji",
  "fullWidthKatakana",
  "fullWidthAlpha",
  "fullWidthDigit",
  "fullWidthSymbol",
  "halfWidthKatakana",
  "halfWidthAlpha",
  "halfWidthDigit",
  "halfWidthSymbolNoQuotes",
  "halfWidthSpace",
];

export const FULL_WIDTH_ONLY_CHAR_TYPES: TCharTypeValidationOptions = [
  "hiragana",
  "kanji",
  "fullWidthKatakana",
  "fullWidthAlpha",
  "fullWidthDigit",
  "fullWidthSymbol",
];

export const ALPHANUMERIC_CHAR_TYPES: TCharTypeValidationOptions = ["halfWidthAlpha", "halfWidthDigit", "fullWidthAlpha", "fullWidthDigit"];

/**
 * checkCharType function based on VBA specification
 * Returns an array of invalid characters (characters not allowed by the patterns)
 *
 * @param text - Input string to validate
 * @param allowedCharTypes - Array of allowed character type keys
 * @returns Array of invalid characters. Empty array means the string is valid.
 */
export function checkCharType(text: string, allowedCharTypes: TCharTypeKey[]): string[] {
  if (!text || !allowedCharTypes || allowedCharTypes.length === 0) {
    return [];
  }

  let remaining = text;

  for (const charType of allowedCharTypes) {
    const { regex } = CHAR_TYPE_MAP[charType];
    remaining = remaining.replace(regex, "");

    if (remaining === "") {
      return [];
    }
  }

  return remaining.split("");
}
